
/**
 * VAT type enum
 * @type {{VAT_NO: number, VAT_LOWER: number, VAT_HIGHER: number, VAT_LOWEST: number}}
 */
export const Vat = {
	VAT_NO : 0,
	VAT_LOWER: 1,
	VAT_HIGHER: 2,
	VAT_LOWEST: 3
};


/**
 * Money class. All calculating method return new instance of money and never modify this instance.
 */
export class Money {
	/**
	 * Create money instance.
	 * @param {number} minorUnits	amount in !!! minor units. Only integer part of the amount is used
 	 * 								because monetary units are not further divisible
	 * @param {string} currency		currency code
	 */
	constructor(minorUnits, currency) {
		this.amount = Math.round(minorUnits);	// only integers allowed here
		this.currency = currency;
	}

	/**
	 * Factory method constructing money from standard units (not minor units).
	 * e.g. 1.5 CZK equals to 150 minor units of CZK, i.e. new Money(150, "CZK").equals(Money.of(1.5, "CZK")) === true
	 */
	static of(amount, currency) {
		const curr = Server.currencies[currency];
		if (curr) {
			return new Money(amount * Math.pow(10, curr.fractionDigits), currency);
		} else throw "Unsupported currency " + currency;
	}

	/**
	 * Factory method for creating Money instance from two DOM inputs.
	 * Example of use: Money.ofInput($('#amountVAT'), $('#currency'))
	 * or just Money.ofInput('amountVAT', 'currency'); with the same result
	 * @param {(string|jQuery)} amountInput		input with amount or its DOM ID
	 * @param {(string|jQuery)} [currencySelect]	input with currency or its DOM ID, if not specified default currency is used
	 * @returns {Money}	money extracted from inputs
	 */
	static ofInput(amountInput, currencySelect) {
		if (typeof amountInput === 'string') amountInput = $('#' + amountInput);
		let currencyCode;
		if (currencySelect) {
			if (typeof currencySelect === 'string') currencySelect = $('#' + currencySelect);
			currencyCode = currencySelect.val();
		} else currencyCode = Server.defaultCurrency;
		return Money.of(Money.parse(amountInput.val()), currencyCode);
	}

	/**
	 * Parse amount from the string
	 * @param {string} moneyStr	money amount as a string (e.g. obtained from HTML input field)
	 * @return {number}
	 */
	static parse(moneyStr) {
		let filtered = '', hasDigit = false;
		if (typeof moneyStr !== "string") return 0;
		for (let ch of moneyStr) {
			if (ch === ',') filtered = filtered + '.';
			else {
				const chIsDigit = "0123456789".indexOf(ch) >= 0;
				if (chIsDigit || ".+-eE".indexOf(ch) >= 0) filtered = filtered + ch;
				hasDigit = chIsDigit || hasDigit;
			}
		}
		if (!hasDigit) return 0;
		let value = parseFloat(filtered);
		if (Number.isNaN(value)) value = 0;
		return value;
	}

	/**
	 * Correct value in a form input field
	 * @param {(string|jQuery)} amountInput
	 * @param {(string|jQuery)} [currencyInput]
	 * @returns {Money} - money extracted from the inputs
	 */
	static correctInput(amountInput, currencyInput) {
		const m = Money.ofInput(amountInput, currencyInput);
		if (typeof amountInput === 'string') amountInput = $('#' + amountInput);
		amountInput.val(m.format());
		return m;
	}

	/**
	 * Return the money formated as a string
	 * @param {number} [digits] - number of fraction digits after floating point to be used
	 * @returns {string}
	 */
	format(digits) {
		const curr = Server.currencies[this.currency];
		if (typeof digits === 'undefined') digits = curr.fractionDigits;
		if (curr) return (this.amount / Math.pow(10, curr.fractionDigits)).toFixed(digits)
	}

	/**
	 * Format JS number to string suitable for sending to server (see Amount.parse() in Java)
	 */
	static formatInput(num) {
		if (typeof num === "number") {
			if (isNaN(num)) return "#";
			let minus;
			if (num < 0) {
				minus = "-";
				num = Math.abs(num);
			} else minus = "";
			const nstr = num.toString();
			const fp = nstr.indexOf(".");
			let beforeFP = nstr.substring(0, fp <= 0 ? nstr.length : fp);
			const afterFP = fp <= 0 ? "" : nstr.substring(fp);
			if (beforeFP.length === 0) beforeFP = "0";
			let fmt = "";
			let digits = 0;
			for (let i = beforeFP.length - 1; i >= 0; i--) {
				const digit = beforeFP.charAt(i);
				if (digits % 3 === 0 && digits > 0) fmt = " " + fmt;
				fmt = digit + fmt;
				digits++;
			}
			return minus + fmt + afterFP;
		}
		if (num == null) return "";
		return num.toString();
	}

	/**
	 * Format percent with one decimal point precision
	 * @param percent - percent (typically between 0.0...1.0, but may be more or even negative)
	 * @returns {string} - formatted (e.g. 0.5031 will become "50.3")
	 */
	static formatPercent(percent) {
		return (Math.round(percent * 1000.0) / 10.0).toString();
	}

	toString() {
		return this.amount + "/" + this.currency;
	}

	/**
	 *
	 * @param money
	 * @return {Money}
	 */
	add(money) {
		if (money instanceof Money) {
			if (this.currency !== money.currency) throw "Cannot add two different currencies "+this.currency+" + "+money.currency;
			return new Money(this.amount + money.amount, this.currency);
		} else throw 'Required Money instance instead of ' + JSON.stringify(money);
	}

	/**
	 *
	 * @param money
	 * @return {Money}
	 */
	subtract(money) {
		if (money instanceof Money) {
			if (this.currency !== money.currency) throw "Cannot subtract two different currencies "+this.currency+" + "+money.currency;
			return new Money(this.amount - money.amount, this.currency);
		} else throw 'Required Money instance instead of ' + JSON.stringify(money);
	}

	/**
	 *
	 * @param multiplier
	 * @param roundFunction
	 * @return {Money}
	 */
	multiply(multiplier, roundFunction) {
		if (typeof roundFunction !== "function") roundFunction = Math.round;
		return new Money(roundFunction(this.amount * multiplier), this.currency);
	}

	/**
	 * Allocate this money into buckets according to passed percentages
	 * @param {number[]} percents	percentages to allocate (e.g. [0.33, 0.33] will result in thirds split
	 * @returns {Money[]}	returned array will be one item longer than percents argument
	 */
	allocate(percents) {
		const alloc = [];
		let remainder = this;
		for (let p of percents) {
			const moneyp = this.multiply(p);
			remainder = remainder.subtract(moneyp);
			alloc.push(moneyp);
		}
		alloc.push(remainder);
		return alloc;
	}

	/**
	 *
	 * @param {Money} money
	 * @return {boolean}
	 */
	equals(money) {
		if (money instanceof Money) {
			return this.amount === money.amount && this.currency === money.currency;
		} else return false;
	}

	/**
	 *
	 * @param {Money} money
	 * @return {boolean}
	 */
	greater(money) {
		if (money instanceof Money) {
			if (this.currency === money.currency) return this.amount > money.amount;
			throw "Cannot compare amounts of two different currencies "+this.currency+" + "+money.currency;
		} else throw 'Money.greater() requires Money instance instead of ' + JSON.stringify(money);
	}

	/**
	 *
	 * @param {Money} money
	 * @return {boolean}
	 */
	greaterOrEqual(money) {
		if (money instanceof Money) {
			if (this.currency === money.currency) return this.amount >= money.amount;
			throw "Cannot compare amounts of two different currencies "+this.currency+" + "+money.currency;
		} else throw 'Money.greaterOrEqual() requires Money instance instead of ' + JSON.stringify(money);
	}

	/**
	 *
	 * @param {Money} money
	 * @return {boolean}
	 */
	lesser(money) {
		if (money instanceof Money) {
			if (this.currency === money.currency) return this.amount < money.amount;
			throw "Cannot compare amounts of two different currencies "+this.currency+" + "+money.currency;
		} else throw 'Money.lesser() requires Money instance instead of ' + JSON.stringify(money);
	}

	/**
	 *
	 * @param {Money} money
	 * @return {boolean}
	 */
	lesserOrEqual(money) {
		if (money instanceof Money) {
			if (this.currency === money.currency) return this.amount <= money.amount;
			throw "Cannot compare amounts of two different currencies "+this.currency+" + "+money.currency;
		} else throw 'Money.lesserOrEqual() requires Money instance instead of ' + JSON.stringify(money);
	}


	/**
	 * Convert the money to default currency using current exchange rates.
	 * @returns {Money}
	 */
	toDefault() {
		if (Server.defaultCurrency === this.currency) return this;
		const curr = Server.currencies[this.currency];
		return Money.of((this.amount / Math.pow(10, curr.fractionDigits))*curr.rate / curr.amount, Server.defaultCurrency);
	}

	/**
	 * Convert money to the specified currency
	 * @param {string} currency	code of one of supported currencies
	 * @returns {Money}
	 */
	convertTo(currency) {
		if (this.currency === currency) return this;
		if (this.currency !== Server.defaultCurrency) return this.toDefault().convertTo(currency);
		// From default currency
		const curr = Server.currencies[currency];
		return Money.of((this.amount / Math.pow(10, curr.fractionDigits)) / curr.rate * curr.amount, currency);
	}

	/**
	 * Calculate selling margin if this money represents purchasing price and parameter selling price
	 * @param {Money} sellingPrice - selling price
	 * @returns {number} - percent of the margin
	 */
	margin(sellingPrice) {
		const price = sellingPrice.convertTo(this.currency);	// unify currencies
		return (price.amount / this.amount) - 1.0;
	}
}


/**
 * Convert money from foreign currency to the default currency.
 * @param {number} foreignMoney	- amount of money in foreign currency
 * @param {string } foreignCurrency	- code of the source foreign currency (e.g. USD, BTC)
 * @returns {number} converted amount
 * @deprecated
 */
export function getMoneyAsDefaultCurr(foreignMoney, foreignCurrency)
{
	if (foreignCurrency === Server.defaultCurrency) return foreignMoney;
	const er = Server.currencies[foreignCurrency];
	return foreignMoney*er.rate/er.amount;
}

/**
 * Convert money from default currency to foreign currency.
 * @param {number} defMoney	- amount of money in default currency
 * @param {string} foreignCurrency - code of the target foreign currency (e.g. USD, BTC)
 * @returns {number} converted amount
 * @deprecated
 */
export function getMoneyAsForeignCurr(defMoney, foreignCurrency)
{
	if (foreignCurrency === Server.defaultCurrency) return defMoney;
	const er = Server.currencies[foreignCurrency];
	return defMoney/er.rate*er.amount;
}

/**
 * Return current VAT rate for given VAT type
 * @param {number|string} vatType	type (0..3) of {@link Vat}
 * @returns {number}	VAT rate
 */
function getVatValue(vatType) {
	if (typeof vatType !== 'number') vatType = parseInt(vatType.toString());
	return Server.VAT[vatType];
}

export function s(form, fname, value)
{
	const input = form.elements[fname];
	let oldVal = input.value;
	let newVal = parseFloat(value);
	if (isNaN(newVal) || isNaN(oldVal)) return;
	if (Math.abs(oldVal-newVal) <= 0.02) return; // too small correction
	let twoDigitsVal = Math.round(newVal*100)/100;
	let fourDigitsVal = Math.round(newVal*10000)/10000;
	if(twoDigitsVal==fourDigitsVal)	input.value = (twoDigitsVal).toFixed(2);
	else input.value = (fourDigitsVal).toFixed(4);
}

export function g(form, fname)
{
	const input = form.elements[fname];
	return input.value;
}

function toVat(amount)
{
	p = parseFloat(amount);
	v = parseFloat(getVatValue());
	if(isNaN(p))
	{
		return amount;
	}
	c = p*(1 + v/100);
	c = Math.round(c*10000)/10000;
	return c;
}

function fromVat(amount)
{
	p = parseFloat(amount);
	v = parseFloat(getVatValue());
	if(isNaN(p))
	{
		return amount;
	}
	c = p/(1 + v/100);
	c = Math.round(c*10000)/10000;
	return c;
}


function mUp(amount, margin)
{
	p = parseFloat(amount);
	m = parseFloat(margin);
	if(isNaN(p) || isNaN(m)) return amount;
	c = p*(1 + m/100);
	c = Math.round(c*10000)/10000;
	return c;
}

function mDown(amount, margin)
{
	p = parseFloat(amount);
	m = parseFloat(margin);
	if(isNaN(p) || isNaN(m)) return amount;
	c = p/(1 + m/100);
	c = Math.round(c*10000)/10000;
	return c;
}

/**
 * Calculates price margin from two price points
 * @param {string|number} lower			lower price (e.g. purchasing price)
 * @param {string|number} higher		higher price
 * @returns {number}	percent (e.g. 20.3)
 */
export function mg(lower, higher)
{
	let l = typeof lower === 'number' ? lower : parseFloat(lower.toString());
	let h = typeof higher === 'number' ? higher : parseFloat(higher.toString());
	if(isNaN(l) || isNaN(h) || l === 0) return 0;
	let m = (h/l - 1)*10000;
	m = Math.round(m)/100;
	return m;
}

/**
 * Reformat number in a form field
 * @param {HTMLFormElement} form	a form containing named field
 * @param {string} fname			input field name
 */
export function correct(form, fname)
{
	const input = form.elements[fname];
	let val = input.value;
	if(val.indexOf(",")>0) val = val.replace(",",".");
	let valNum = parseFloat(val);
	if (isNaN(valNum)) valNum = 0;
	let twoDigitsVal = Math.round(valNum*100)/100;
	let fourDigitsVal = Math.round(valNum*10000)/10000;
	if(twoDigitsVal === fourDigitsVal)	input.value = (twoDigitsVal).toFixed(2);
	else input.value = (fourDigitsVal).toFixed(4);
}

